/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  FloppyDisk, Plus, Trash, ArrowSquareOut, Image as ImgIcon,
  ArrowClockwise, Sparkle,
} from "@phosphor-icons/react";

const SECTIONS = [
  { key: "hero",    title: "Hero" },
  { key: "ceo",     title: "Founder / CEO" },
  { key: "legacy",  title: "Legacy section" },
  { key: "vision",  title: "Vision section" },
  { key: "impact",  title: "Impact stats" },
  { key: "pillars", title: "Pillars" },
  { key: "cta",     title: "Call-to-action" },
];

export default function AdminAboutEditor() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("hero");

  const load = async () => {
    try {
      const r = await api.get("/admin/about");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setData(d => ({ ...d, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      // Strip server-only fields
      const { tenant_id, updated_at, ...payload } = data;
      await api.put("/admin/about", payload);
      toast.success("About page saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (!data) return <AppShell title="About Us · Editor" subtitle="Marketing CMS"><div className="p-6 text-sm text-[#8A8780]">Loading…</div></AppShell>;

  return (
    <AppShell title="About Us · Editor" subtitle="Marketing CMS">
      {/* Top action bar */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="about-editor-toolbar">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Public URL</div>
          <a href="/about" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D5A27] hover:underline">
            /about <ArrowSquareOut size={11}/>
          </a>
        </div>
        <div className="text-xs text-[#8A8780] hidden md:block">
          Last saved · {data.updated_at ? new Date(data.updated_at).toLocaleString() : "Never"}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={load} className="h-9 px-3 rounded text-xs border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55] inline-flex items-center gap-1.5">
            <ArrowClockwise size={11}/> Reload
          </button>
          <button onClick={save} disabled={busy} data-testid="about-save"
                   className="h-9 px-4 rounded text-xs font-medium bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white inline-flex items-center gap-1.5">
            <FloppyDisk size={11}/> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto" data-testid="about-tabs">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`about-tab-${s.key}`}
                   className={`h-9 px-3 rounded text-xs font-medium whitespace-nowrap ${tab === s.key ? "bg-[#2D5A27] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]"}`}>
            {s.title}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
        {tab === "hero" && (
          <div className="grid grid-cols-1 gap-3">
            <Field label="Eyebrow" v={data.hero_eyebrow} onChange={v => set({ hero_eyebrow: v })} testid="hero-eyebrow"/>
            <Field label="Hero title" v={data.hero_title} onChange={v => set({ hero_title: v })} multiline rows={2} testid="hero-title"/>
            <Field label="Subtitle" v={data.hero_subtitle} onChange={v => set({ hero_subtitle: v })} multiline rows={2}/>
          </div>
        )}

        {tab === "ceo" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" v={data.ceo_name} onChange={v => set({ ceo_name: v })}/>
            <Field label="Title / role" v={data.ceo_title} onChange={v => set({ ceo_title: v })}/>
            <Field label="Photo URL" v={data.ceo_photo_url} onChange={v => set({ ceo_photo_url: v })} wide
                    icon={ImgIcon}/>
            {data.ceo_photo_url && (
              <div className="col-span-2 flex items-center gap-3">
                <img src={data.ceo_photo_url} alt="" className="w-24 h-28 rounded-xl object-cover ring-2 ring-[#E5E2D9]"/>
                <div className="text-[11px] text-[#8A8780]">Preview · 4:5 portrait recommended</div>
              </div>
            )}
            <Field label="Section title" v={data.ceo_section_title} onChange={v => set({ ceo_section_title: v })} wide/>
            <Field label="Section body (use blank lines to split paragraphs)" v={data.ceo_section_body} onChange={v => set({ ceo_section_body: v })} multiline rows={8} wide/>
            <Field label="Quote" v={data.ceo_quote} onChange={v => set({ ceo_quote: v })} multiline rows={3} wide/>
          </div>
        )}

        {tab === "legacy" && (
          <div className="grid grid-cols-1 gap-3">
            <Field label="Section title" v={data.legacy_section_title} onChange={v => set({ legacy_section_title: v })}/>
            <Field label="Section body" v={data.legacy_section_body} onChange={v => set({ legacy_section_body: v })} multiline rows={6}/>
          </div>
        )}

        {tab === "vision" && (
          <div className="grid grid-cols-1 gap-3">
            <Field label="Section title" v={data.vision_section_title} onChange={v => set({ vision_section_title: v })}/>
            <Field label="Section body" v={data.vision_section_body} onChange={v => set({ vision_section_body: v })} multiline rows={8}/>
          </div>
        )}

        {tab === "impact" && (
          <ListEditor items={data.impact_stats || []}
                       onChange={(items) => set({ impact_stats: items })}
                       blank={{ value: "", label: "", sub: "" }}
                       fields={[
                         { k: "value", lbl: "Value (e.g. 5,000+)" },
                         { k: "label", lbl: "Label" },
                         { k: "sub",   lbl: "Subtext (optional)" },
                       ]}
                       testid="impact"/>
        )}

        {tab === "pillars" && (
          <ListEditor items={data.pillars || []}
                       onChange={(items) => set({ pillars: items })}
                       blank={{ title: "", body: "" }}
                       fields={[
                         { k: "title", lbl: "Pillar title" },
                         { k: "body",  lbl: "Pillar body", multiline: true, rows: 3 },
                       ]}
                       testid="pillars"/>
        )}

        {tab === "cta" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA title" v={data.cta_title} onChange={v => set({ cta_title: v })} wide/>
            <Field label="CTA body" v={data.cta_body} onChange={v => set({ cta_body: v })} multiline rows={3} wide/>
            <Field label="Primary button label" v={data.cta_primary_label} onChange={v => set({ cta_primary_label: v })}/>
            <Field label="Primary button URL" v={data.cta_primary_to} onChange={v => set({ cta_primary_to: v })}/>
            <Field label="Secondary button label" v={data.cta_secondary_label} onChange={v => set({ cta_secondary_label: v })}/>
            <Field label="Secondary button URL" v={data.cta_secondary_to} onChange={v => set({ cta_secondary_to: v })}/>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[#F0ECE3] flex items-center justify-between text-xs text-[#8A8780]">
          <div className="inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="duotone" color="#2D5A27"/>
            All fields are optional — empty fields fall back to the platform default copy.
          </div>
          <button onClick={save} disabled={busy} className="h-8 px-3 rounded text-xs font-medium bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

// =====================================================================
// Helpers
// =====================================================================
function Field({ label, v, onChange, multiline = false, rows = 3, wide = false, testid, icon: Icon }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] flex items-center gap-1.5">
        {Icon && <Icon size={11}/>} {label}
      </label>
      {multiline ? (
        <textarea value={v ?? ""} onChange={e => onChange(e.target.value)} rows={rows}
                   data-testid={testid}
                   className="mt-1 w-full border border-[#E5E2D9] rounded px-2 py-1.5 text-sm font-mono"/>
      ) : (
        <input value={v ?? ""} onChange={e => onChange(e.target.value)}
                data-testid={testid}
                className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm"/>
      )}
    </div>
  );
}

function ListEditor({ items, onChange, blank, fields, testid }) {
  const upd = (i, k, v) => {
    const next = items.slice();
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const rm = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, { ...blank }]);
  return (
    <div className="space-y-3" data-testid={`${testid}-editor`}>
      {items.map((it, i) => (
        <div key={i} className="border border-[#E5E2D9] rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Item {i + 1}</div>
            <button onClick={() => rm(i)} className="text-[#7A2E18] hover:text-[#9A3A20] inline-flex items-center gap-1 text-xs">
              <Trash size={11}/> Remove
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {fields.map(f => (
              <Field key={f.k} label={f.lbl} v={it[f.k]} onChange={v => upd(i, f.k, v)}
                      multiline={!!f.multiline} rows={f.rows || 3}
                      wide={fields.length === 2 && !!f.multiline}/>
            ))}
          </div>
        </div>
      ))}
      <button onClick={add} data-testid={`${testid}-add`}
               className="h-9 px-3 rounded text-xs border border-dashed border-[#2D5A27] text-[#2D5A27] inline-flex items-center gap-1.5">
        <Plus size={11}/> Add item
      </button>
    </div>
  );
}
